import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaEdit } from "react-icons/fa";
import { Trash2, Plus, Check, X } from "lucide-react";

import styles from "./Pillars.module.scss";
import { useLanguage } from "../../context/languageContext";
import { usePermissions, Can } from "../../context/permissionContext";
import { confirmCascadeDelete } from "../../helper/confirmCascadeDelete";
import { useAlreadyExists } from "../../components/confirmDialog";

const API = process.env.REACT_APP_API_ROOT;

/**
 * One pillar and its sub-pillars, managed inline.
 *
 * Sub-pillar add/rename/delete all hit `/pillar/:pillarId/sub-pillar/...` and
 * get the whole updated pillar back, which is handed straight to
 * `onPillarUpdated`. No re-fetch, so the card can't flicker or lose its place.
 *
 * Renaming happens in place (the chip becomes an input) rather than in a modal —
 * a sub-pillar is one word, and a modal for one word is a lot of ceremony.
 */
const PillarCard = ({ pillar, onEdit, onDelete, onPillarUpdated }) => {
    const { adminInfo } = useLanguage();
    const showAlreadyExists = useAlreadyExists();
    const { can } = usePermissions();

    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState("");
    // Sub-pillar id currently being renamed, or null.
    const [editingSubId, setEditingSubId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const [busy, setBusy] = useState(false);

    const canEdit = can("pillars", "edit");
    const headers = { Authorization: `Bearer ${adminInfo?.token}` };

    // Every sub-pillar write shares this: one request, one updated pillar back,
    // one toast on failure. Returns true so callers know whether to reset state.
    const runSubPillarRequest = async (method, url, payload, failure) => {
        setBusy(true);
        try {
            const { data: response } = await axios[method](url, payload, { headers });
            if (response.status) {
                if (response.data) onPillarUpdated(response.data);
                return true;
            }
            toast.error(response.message || failure);
            return false;
        } catch (error) {
            console.error(`${failure} ::`, error);
            if (!showAlreadyExists(error)) toast.error(error.response?.data?.error || failure);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const handleAddSubPillar = async () => {
        const name = newName.trim();
        if (!name) return;

        const ok = await runSubPillarRequest(
            "post",
            `${API}/api/admin/pillar/${pillar.pillarId}/sub-pillar/add`,
            { name },
            "Could not add the sub pillar."
        );
        if (ok) {
            toast.success(`"${name}" added to ${pillar.name}.`);
            // Stay open — adding sub-pillars is usually done several at a time.
            setNewName("");
        }
    };

    const handleRenameSubPillar = async (sub) => {
        const name = editingName.trim();
        if (!name || name === sub.name) {
            setEditingSubId(null);
            return;
        }

        const ok = await runSubPillarRequest(
            "put",
            `${API}/api/admin/pillar/${pillar.pillarId}/sub-pillar/update/${sub.subPillarId}`,
            { name },
            "Could not rename the sub pillar."
        );
        if (ok) setEditingSubId(null);
    };

    const handleDeleteSubPillar = async (sub) => {
        if (!(await confirmCascadeDelete({
            source: "subPillar",
            ids: [sub.subPillarId],
            what: `"${sub.name}" from ${pillar.name}`,
            headers,
        }))) return;

        const ok = await runSubPillarRequest(
            "put",
            `${API}/api/admin/pillar/${pillar.pillarId}/sub-pillar/delete/${sub.subPillarId}`,
            {},
            "Could not remove the sub pillar."
        );
        if (ok) toast.success(`"${sub.name}" removed.`);
    };

    const subPillars = pillar.subPillars || [];

    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <div>
                    <h6 className={styles.pillarName}>{pillar.name}</h6>
                    <small className={styles.count}>
                        {subPillars.length === 0
                            ? "No sub pillars yet"
                            : `${subPillars.length} sub pillar${subPillars.length === 1 ? "" : "s"}`}
                    </small>
                </div>
                <div className={styles.cardActions}>
                    <Can feature="pillars" action="edit">
                        <div title="Rename pillar" onClick={onEdit}>
                            <FaEdit size={18} />
                        </div>
                    </Can>
                    <Can feature="pillars" action="delete">
                        <div title="Delete pillar" className={styles.deleteAction} onClick={onDelete}>
                            <Trash2 size={18} />
                        </div>
                    </Can>
                </div>
            </div>

            <div className={styles.chips}>
                {subPillars.map((sub) =>
                    editingSubId === sub.subPillarId ? (
                        <span key={sub.subPillarId} className={styles.chipEditing}>
                            <input
                                autoFocus
                                className={styles.chipInput}
                                value={editingName}
                                disabled={busy}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleRenameSubPillar(sub);
                                    if (e.key === "Escape") setEditingSubId(null);
                                }}
                            />
                            <Check
                                size={14}
                                className={styles.chipIcon}
                                onClick={() => handleRenameSubPillar(sub)}
                            />
                            <X
                                size={14}
                                className={styles.chipIcon}
                                onClick={() => setEditingSubId(null)}
                            />
                        </span>
                    ) : (
                        <span key={sub.subPillarId} className={styles.chip}>
                            <span
                                className={canEdit ? styles.chipLabelEditable : undefined}
                                title={canEdit ? "Click to rename" : undefined}
                                onClick={() => {
                                    if (!canEdit) return;
                                    setEditingSubId(sub.subPillarId);
                                    setEditingName(sub.name);
                                }}
                            >
                                {sub.name}
                            </span>
                            <Can feature="pillars" action="delete">
                                <X
                                    size={14}
                                    className={styles.chipRemove}
                                    onClick={() => handleDeleteSubPillar(sub)}
                                />
                            </Can>
                        </span>
                    )
                )}

                <Can feature="pillars" action="edit">
                    {adding ? (
                        <span className={styles.chipEditing}>
                            <input
                                autoFocus
                                className={styles.chipInput}
                                placeholder="Sub pillar name"
                                value={newName}
                                disabled={busy}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleAddSubPillar();
                                    if (e.key === "Escape") { setAdding(false); setNewName(""); }
                                }}
                            />
                            <Check size={14} className={styles.chipIcon} onClick={handleAddSubPillar} />
                            <X
                                size={14}
                                className={styles.chipIcon}
                                onClick={() => { setAdding(false); setNewName(""); }}
                            />
                        </span>
                    ) : (
                        <button
                            type="button"
                            className={styles.addChip}
                            onClick={() => setAdding(true)}
                        >
                            <Plus size={14} /> Add sub pillar
                        </button>
                    )}
                </Can>
            </div>
        </div>
    );
};

export default PillarCard;
